import React, { useState } from 'react';
import {
  Box,
  Container,
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
} from '@mui/material';
import { SearchOutlined, FlightTakeoff, FlightLand } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { FadeIn, SlideIn } from '../../animations';
import { DateRangeInput, GuestSelector } from '../../ui/FormInputs';
import { BRAND } from '../../../brand';
import { colors } from '../../theme/colors';
import {
  coerceFlightSearchAirports,
  getCountriesForProductLine,
  getDefaultDestinationCountry,
  getDestinationAirports,
  getFlightScopeOptions,
  getOriginAirports,
} from '../../../utils/locationHierarchy';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=2000&q=80';

const HeroShell = styled(motion.section)(({ theme }) => ({
  position: 'relative',
  minHeight: '78vh',
  display: 'flex',
  alignItems: 'flex-end',
  overflow: 'visible', // allow date calendar to open outside hero
  backgroundColor: colors.background.light,
  [theme.breakpoints.down('md')]: {
    minHeight: '85vh',
    alignItems: 'center',
  },
}));

const HeroMedia = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  backgroundImage: `url(${HERO_IMAGE})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center 40%',
});

const HeroScrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  background: colors.gradient.hero,
});

const SearchPanel = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 30,
  backgroundColor: '#fff',
  borderRadius: theme.custom?.borderRadius?.lg || 16,
  padding: theme.spacing(2),
  border: `1px solid ${colors.border.light}`,
  boxShadow: '0 12px 40px rgba(14, 28, 42, 0.08)',
  overflow: 'visible',
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2.5),
  },
}));

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'white',
    height: 52,
  },
};

const toYmd = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  // Use local calendar date — toISOString() shifts the day in PK (UTC+5)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const HeroSearch = ({ onSearch }) => {
  const navigate = useNavigate();
  const [flightScope, setFlightScope] = useState('International');
  const [destinationCountry, setDestinationCountry] = useState('AE');
  const [origin, setOrigin] = useState('ISB');
  const [destination, setDestination] = useState('JED');
  const originAirports = getOriginAirports();
  const destinationAirports = getDestinationAirports(flightScope, destinationCountry);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState({ adults: 1, children: 0, total: 1 });
  const [error, setError] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    setError('');

    if (!origin || !destination) {
      setError('Please select From and To airports');
      return;
    }
    if (origin === destination) {
      setError('From and To must be different');
      return;
    }
    if (!checkIn) {
      setError('Please select travel dates');
      return;
    }

    const searchPayload = {
      origin,
      destination,
      departureDate: toYmd(checkIn),
      returnDate: checkOut ? toYmd(checkOut) : '',
      adults: guests.adults || 1,
      children: guests.children || 0,
      infants: 0,
      cabinClass: 'economy',
      autoSearch: true,
      flightScope,
      destinationCountry,
    };

    if (onSearch) {
      onSearch(searchPayload);
      return;
    }

    navigate('/v2/flights', { state: searchPayload });
  };

  const onScopeChange = (scope) => {
    setFlightScope(scope);
    const country = getDefaultDestinationCountry(scope);
    setDestinationCountry(country);
    const coerced = coerceFlightSearchAirports(scope, origin, destination, country);
    setOrigin(coerced.origin);
    setDestination(coerced.destination);
  };

  const onCountryChange = (country) => {
    setDestinationCountry(country);
    const coerced = coerceFlightSearchAirports(flightScope, origin, destination, country);
    setOrigin(coerced.origin);
    setDestination(coerced.destination);
  };

  return (
    <HeroShell
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <HeroMedia
        component={motion.div}
        initial={{ scale: 1.06 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.35, ease: [0.22, 1, 0.36, 1] }}
      />
      <HeroScrim />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 20,
          pb: { xs: 5, md: 7 },
          pt: { xs: 4, md: 5 },
          overflow: 'visible',
        }}
      >
        <Box sx={{ maxWidth: 600, mb: { xs: 3, md: 4 } }}>
          <FadeIn direction="up" delay={0.1}>
            <Typography
              component="p"
              sx={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: { xs: '2rem', sm: '2.6rem', md: '3.15rem' },
                fontWeight: 600,
                color: colors.primary.main,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                mb: 1,
              }}
            >
              {BRAND.name}
            </Typography>
          </FadeIn>

          <FadeIn direction="up" delay={0.2}>
            <Typography
              variant="h1"
              sx={{
                color: colors.primary.main,
                fontSize: { xs: '1.45rem', sm: '1.85rem', md: '2.15rem' },
                fontWeight: 500,
                mb: 1.5,
                maxWidth: 480,
              }}
            >
              Trips worldwide. Planned with care.
            </Typography>
          </FadeIn>

          <FadeIn direction="up" delay={0.3}>
            <Typography
              sx={{
                color: colors.text.secondary,
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: 440,
                lineHeight: 1.55,
              }}
            >
              Umrah, ziyarat, holidays, and Pakistan journeys — hotels, transport,
              and meals in one place.
            </Typography>
          </FadeIn>
        </Box>

        <SlideIn direction="up" delay={0.35}>
          <SearchPanel component="form" onSubmit={handleSearch}>
            <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Trip type"
                  value={flightScope}
                  onChange={(e) => onScopeChange(e.target.value)}
                  sx={fieldSx}
                >
                  {getFlightScopeOptions().map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {getCountriesForProductLine(flightScope).length > 1 && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Destination country"
                    value={destinationCountry}
                    onChange={(e) => onCountryChange(e.target.value)}
                    sx={fieldSx}
                  >
                    {getCountriesForProductLine(flightScope).map((c) => (
                      <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="From"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: <FlightTakeoff sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />,
                  }}
                >
                  {originAirports.map((a) => (
                    <MenuItem key={a.code} value={a.code} disabled={a.code === destination}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="To"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: <FlightLand sx={{ mr: 1, color: 'action.active', fontSize: 20 }} />,
                  }}
                >
                  {destinationAirports.map((a) => (
                    <MenuItem key={`to-${a.code}`} value={a.code} disabled={a.code === origin}>
                      {a.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DateRangeInput
                  startDate={checkIn}
                  endDate={checkOut}
                  onStartDateChange={setCheckIn}
                  onEndDateChange={setCheckOut}
                  minDate={startOfToday()}
                  emptyLabel="Depart — Return"
                  startLabel="Depart"
                  endLabel="Return"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <GuestSelector
                  adults={guests.adults}
                  children={guests.children}
                  onGuestsChange={setGuests}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  size="large"
                  startIcon={<SearchOutlined />}
                  sx={{ height: 52 }}
                >
                  Search flights
                </Button>
              </Grid>
            </Grid>

            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>
                {error}
              </Typography>
            )}
          </SearchPanel>
        </SlideIn>
      </Container>
    </HeroShell>
  );
};

export default HeroSearch;
